/*
 * @Author: 赵泽文
 * @Date: 2025-12-04 17:31:53
 * @LastEditors: 赵泽文
 * @LastEditTime: 2026-02-05 15:12:41
 * @Description: 国际化配置
 */
import type { IntlShape } from '@formatjs/intl';
import { createIntl, createIntlCache } from '@formatjs/intl';
import { mergeIntlMessages, setGlobalPackageIntlMessages } from '@monorepo/i18n';
import { localCache } from '@monorepo/utils';
import zhCNMessages from '../../public/lang/zh-CN.json';
import enUSMessages from '../../public/lang/en-US.json';

export const intlCache = createIntlCache();

const DEFAULT_LOCALE = 'zh-CN';
const getLocale = () => {
  return localCache.getItem('MarsLanguage') === 'en' ? 'en-US' : DEFAULT_LOCALE;
};

const getLocaleMessages = () => {
  return mergeIntlMessages(zhCNMessages, enUSMessages, getLocale());
};

// 创建 $t 包装函数
// 注意：formatjs 的 babel 插件需要在编译时静态提取 defaultMessage
// 必须使用对象格式 { defaultMessage: '...' }，babel 插件才能识别并提取
const createFormatMessage = (intlInstance: IntlShape) => {
  return (
    descriptor: { id?: string; defaultMessage?: string; [key: string]: any },
    values?: Record<string, any>,
  ) => {
    return intlInstance.formatMessage(descriptor, values);
  };
};

/** 同步依赖国际化的 CSS 变量（如 ag-grid 合计行伪元素文案） */
const syncIntlCssVariables = (formatMessage: ReturnType<typeof createFormatMessage>) => {
  if (typeof document === 'undefined') {
    return;
  }
  const totalText = formatMessage({ defaultMessage: '总计' });
  document.documentElement.style.setProperty('--mars-total-text', JSON.stringify(totalText));
};

// 初始化或重新初始化 intl 实例
const initIntl = () => {
  const locale = getLocale();
  const messages = getLocaleMessages();
  setGlobalPackageIntlMessages(locale);
  const newIntl = createIntl(
    {
      locale,
      messages,
      // 当翻译缺失时，使用 defaultMessage 而不报错
      onError: (err) => {
        // 只忽略 MISSING_TRANSLATION 错误，其他错误仍然输出
        if (err.code !== 'MISSING_TRANSLATION') {
          // eslint-disable-next-line no-console
          console.warn('[intl]', err);
        }
      },
    },
    intlCache,
  );
  intl = newIntl;
  $t = createFormatMessage(newIntl);
  // 挂载到全局变量，方便所有页面直接使用，无需导入
  (window as any).$t = $t;
  syncIntlCssVariables($t);
  return newIntl;
};

export let intl = createIntl(
  {
    locale: getLocale(),
    messages: getLocaleMessages(),
    // 当翻译缺失时，使用 defaultMessage 而不报错
    onError: (err) => {
      // 只忽略 MISSING_TRANSLATION 错误，其他错误仍然输出
      if (err.code !== 'MISSING_TRANSLATION') {
        // eslint-disable-next-line no-console
        console.warn('[intl]', err);
      }
    },
  },
  intlCache,
);

export let $t = createFormatMessage(intl);

// 初始化时挂载到全局
setGlobalPackageIntlMessages(getLocale());
(window as any).$t = $t;
syncIntlCssVariables($t);

// 导出初始化函数，供子应用 mount 时调用
export const initIntlMessages = initIntl;

export function setIntl(res: IntlShape) {
  intl = res;
  $t = createFormatMessage(res);
  // 更新全局变量
  (window as any).$t = $t;
  syncIntlCssVariables($t);
}
